/**
 * Advanced Access Control Service
 *
 * Provides biometric authentication, geofencing, time-based restrictions,
 * need-to-know enforcement, and temporary access grants.
 */

export interface AccessPolicy {
  id: string;
  name: string;
  description: string;
  type: PolicyType;
  priority: number;  // Lower = higher priority
  isActive: boolean;
  scope: PolicyScope;
  conditions: AccessCondition[];
  actions: PolicyAction[];
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  expiresAt?: Date;
}

export type PolicyType =
  | 'role_based'
  | 'attribute_based'
  | 'context_based'
  | 'temporary'
  | 'emergency';

export interface PolicyScope {
  mdas?: string[];
  departments?: string[];
  grades?: string[];
  recordTypes?: string[];
  sensitivityLevels?: SensitivityLevel[];
  specificRecords?: string[];
}

export type SensitivityLevel =
  | 'public'
  | 'internal'
  | 'confidential'
  | 'secret'
  | 'top_secret';

export interface AccessCondition {
  id: string;
  type: ConditionType;
  operator: 'equals' | 'not_equals' | 'in' | 'not_in' | 'greater_than' | 'less_than' | 'between' | 'matches';
  value: unknown;
  isRequired: boolean;
}

export type ConditionType =
  | 'role'
  | 'grade'
  | 'department'
  | 'mda'
  | 'time_of_day'
  | 'day_of_week'
  | 'date_range'
  | 'location'
  | 'ip_address'
  | 'device_type'
  | 'authentication_method'
  | 'session_duration'
  | 'access_frequency'
  | 'clearance_level'
  | 'purpose';

export interface PolicyAction {
  type: 'allow' | 'deny' | 'require_mfa' | 'require_approval' | 'log_only' | 'alert';
  scope: 'view' | 'edit' | 'delete' | 'print' | 'export' | 'share' | 'all';
  restrictions?: {
    maxViews?: number;
    maxDownloads?: number;
    watermark?: boolean;
    preventPrint?: boolean;
    preventCopy?: boolean;
    expiresAfter?: number;  // minutes
  };
}

export interface AccessRequest {
  id: string;
  requesterId: string;
  requesterName: string;
  requesterRole: string;
  requesterMDA: string;
  resourceType: 'pfile' | 'document' | 'report';
  resourceId: string;
  resourceName: string;
  accessType: 'view' | 'edit' | 'delete' | 'print' | 'export' | 'share';
  context: AccessContext;
  requestedAt: Date;
  status: 'pending' | 'evaluating' | 'approved' | 'denied' | 'expired';
  decision?: AccessDecision;
}

export interface AccessContext {
  ipAddress: string;
  deviceId?: string;
  deviceType: 'desktop' | 'mobile' | 'tablet';
  browser?: string;
  operatingSystem?: string;
  location?: GeoLocation;
  authenticationMethod: AuthenticationMethod;
  sessionId: string;
  sessionStartTime: Date;
  purpose?: string;
  urgency?: 'normal' | 'urgent' | 'emergency';
}

export interface GeoLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  isWithinGeofence?: boolean;
  geofenceName?: string;
}

export type AuthenticationMethod =
  | 'password'
  | 'password_mfa'
  | 'biometric'
  | 'smart_card'
  | 'otp'
  | 'sso'
  | 'emergency_override';

export interface AccessDecision {
  allowed: boolean;
  reason: string;
  policyId?: string;
  policyName?: string;
  conditions: ConditionEvaluation[];
  restrictions?: PolicyAction['restrictions'];
  requiresApproval?: boolean;
  approverRole?: string;
  validUntil?: Date;
  auditId: string;
}

export interface ConditionEvaluation {
  conditionId: string;
  conditionType: ConditionType;
  expected: unknown;
  actual: unknown;
  passed: boolean;
  message?: string;
}

export interface Geofence {
  id: string;
  name: string;
  type: 'circle' | 'polygon';
  center?: { latitude: number; longitude: number };
  radius?: number;  // meters (for circle)
  polygon?: Array<{ latitude: number; longitude: number }>;
  description: string;
  mdas: string[];
  isActive: boolean;
}

export interface TemporaryAccessGrant {
  id: string;
  grantedTo: string;
  grantedToName: string;
  grantedBy: string;
  grantedByName: string;
  resourceType: 'pfile' | 'document' | 'collection';
  resourceId: string;
  resourceName: string;
  accessLevel: 'view' | 'edit' | 'full';
  reason: string;
  grantedAt: Date;
  expiresAt: Date;
  isActive: boolean;
  revokedAt?: Date;
  revokedBy?: string;
  revokeReason?: string;
  accessCount: number;
  lastAccessedAt?: Date;
  maxAccesses?: number;
  restrictions?: PolicyAction['restrictions'];
}

export interface BiometricVerification {
  id: string;
  userId: string;
  method: 'fingerprint' | 'face' | 'iris' | 'voice';
  verifiedAt: Date;
  deviceId: string;
  confidence: number;  // 0-100
  isVerified: boolean;
  failureReason?: string;
}

export interface SecurityIncident {
  id: string;
  type: IncidentType;
  severity: 'low' | 'medium' | 'high' | 'critical';
  timestamp: Date;
  userId?: string;
  userName?: string;
  resourceId?: string;
  resourceName?: string;
  description: string;
  ipAddress?: string;
  location?: GeoLocation;
  isResolved: boolean;
  resolvedAt?: Date;
  resolvedBy?: string;
  resolution?: string;
  escalated: boolean;
  escalatedTo?: string;
}

export type IncidentType =
  | 'unauthorized_access'
  | 'failed_authentication'
  | 'suspicious_activity'
  | 'policy_violation'
  | 'geofence_breach'
  | 'after_hours_access'
  | 'bulk_download'
  | 'privilege_escalation'
  | 'session_hijack'
  | 'brute_force';

class AccessControlService {
  private readonly defaultGeofences: Geofence[] = [
    {
      id: 'gf_accra_hq',
      name: 'OHCS Headquarters',
      type: 'circle',
      center: { latitude: 5.5600, longitude: -0.2050 },
      radius: 500,
      description: 'OHCS Head Office, Accra',
      mdas: ['OHCS'],
      isActive: true,
    },
    {
      id: 'gf_ministries',
      name: 'Ministries Enclave',
      type: 'circle',
      center: { latitude: 5.5500, longitude: -0.2000 },
      radius: 2000,
      description: 'Government Ministries Area, Accra',
      mdas: ['ALL'],
      isActive: true,
    },
  ];

  /**
   * Evaluate access request
   */
  evaluateAccess(
    request: AccessRequest,
    policies: AccessPolicy[]
  ): AccessDecision {
    const conditionEvaluations: ConditionEvaluation[] = [];
    let finalDecision: AccessDecision | null = null;

    // Sort policies by priority
    const sortedPolicies = [...policies]
      .filter(p => p.isActive)
      .sort((a, b) => a.priority - b.priority);

    for (const policy of sortedPolicies) {
      // Check if policy scope applies
      if (!this.policyScopeApplies(policy, request)) {
        continue;
      }

      // Evaluate all conditions
      const evaluations = policy.conditions.map(condition =>
        this.evaluateCondition(condition, request)
      );
      conditionEvaluations.push(...evaluations);

      // Check if all required conditions are met
      const requiredConditions = policy.conditions.filter(c => c.isRequired);
      const allRequiredMet = requiredConditions.every(c => {
        const eval_ = evaluations.find(e => e.conditionId === c.id);
        return eval_?.passed;
      });

      if (allRequiredMet) {
        // Apply first matching policy action
        const action = policy.actions.find(a =>
          a.scope === 'all' || a.scope === request.accessType
        );

        if (action) {
          finalDecision = {
            allowed: action.type === 'allow',
            reason: action.type === 'allow'
              ? `Access granted by policy: ${policy.name}`
              : `Access denied by policy: ${policy.name}`,
            policyId: policy.id,
            policyName: policy.name,
            conditions: conditionEvaluations,
            restrictions: action.restrictions,
            requiresApproval: action.type === 'require_approval',
            approverRole: action.type === 'require_approval' ? 'supervisor' : undefined,
            validUntil: action.restrictions?.expiresAfter
              ? new Date(Date.now() + action.restrictions.expiresAfter * 60000)
              : undefined,
            auditId: `audit_${Date.now()}`,
          };
          break;
        }
      }
    }

    // Default deny if no policy matched
    if (!finalDecision) {
      finalDecision = {
        allowed: false,
        reason: 'No matching access policy found - access denied by default',
        conditions: conditionEvaluations,
        auditId: `audit_${Date.now()}`,
      };
    }

    return finalDecision;
  }

  /**
   * Check if policy scope applies to request
   */
  private policyScopeApplies(policy: AccessPolicy, request: AccessRequest): boolean {
    const scope = policy.scope;

    if (scope.mdas && !scope.mdas.includes(request.requesterMDA)) {
      return false;
    }

    // Additional scope checks would go here
    return true;
  }

  /**
   * Evaluate single condition
   */
  private evaluateCondition(
    condition: AccessCondition,
    request: AccessRequest
  ): ConditionEvaluation {
    const context = request.context;
    let actual: unknown;
    let passed = false;

    switch (condition.type) {
      case 'role':
        actual = request.requesterRole;
        passed = this.compareValues(condition.operator, actual, condition.value);
        break;

      case 'mda':
        actual = request.requesterMDA;
        passed = this.compareValues(condition.operator, actual, condition.value);
        break;

      case 'time_of_day':
        const hour = new Date().getHours();
        actual = hour;
        passed = this.compareValues(condition.operator, hour, condition.value);
        break;

      case 'day_of_week':
        const day = new Date().getDay();
        actual = day;
        passed = this.compareValues(condition.operator, day, condition.value);
        break;

      case 'location':
        if (context.location) {
          actual = context.location;
          passed = this.checkGeofence(context.location, condition.value as string);
        } else {
          actual = 'unknown';
          passed = false;
        }
        break;

      case 'ip_address':
        actual = context.ipAddress;
        passed = this.compareValues(condition.operator, actual, condition.value);
        break;

      case 'device_type':
        actual = context.deviceType;
        passed = this.compareValues(condition.operator, actual, condition.value);
        break;

      case 'authentication_method':
        actual = context.authenticationMethod;
        passed = this.compareValues(condition.operator, actual, condition.value);
        break;

      case 'session_duration':
        const sessionMinutes = (Date.now() - context.sessionStartTime.getTime()) / 60000;
        actual = sessionMinutes;
        passed = this.compareValues(condition.operator, sessionMinutes, condition.value);
        break;

      default:
        actual = 'unknown';
        passed = false;
    }

    return {
      conditionId: condition.id,
      conditionType: condition.type,
      expected: condition.value,
      actual,
      passed,
      message: passed
        ? `Condition "${condition.type}" satisfied`
        : `Condition "${condition.type}" not met: expected ${JSON.stringify(condition.value)}, got ${JSON.stringify(actual)}`,
    };
  }

  /**
   * Compare values based on operator
   */
  private compareValues(
    operator: AccessCondition['operator'],
    actual: unknown,
    expected: unknown
  ): boolean {
    switch (operator) {
      case 'equals':
        return actual === expected;
      case 'not_equals':
        return actual !== expected;
      case 'in':
        return Array.isArray(expected) && expected.includes(actual);
      case 'not_in':
        return Array.isArray(expected) && !expected.includes(actual);
      case 'greater_than':
        return typeof actual === 'number' && typeof expected === 'number' && actual > expected;
      case 'less_than':
        return typeof actual === 'number' && typeof expected === 'number' && actual < expected;
      case 'between':
        if (typeof actual === 'number' && Array.isArray(expected) && expected.length === 2) {
          return actual >= expected[0] && actual <= expected[1];
        }
        return false;
      case 'matches':
        return typeof actual === 'string' && typeof expected === 'string' &&
          new RegExp(expected).test(actual);
      default:
        return false;
    }
  }

  /**
   * Check if location is within geofence
   */
  checkGeofence(location: GeoLocation, geofenceId: string): boolean {
    const geofence = this.defaultGeofences.find(g => g.id === geofenceId);
    if (!geofence || !geofence.isActive) return false;

    if (geofence.type === 'circle' && geofence.center && geofence.radius) {
      const distance = this.calculateDistance(
        location.latitude,
        location.longitude,
        geofence.center.latitude,
        geofence.center.longitude
      );
      return distance <= geofence.radius;
    }

    // Polygon check would go here
    return false;
  }

  /**
   * Calculate distance between two points (Haversine formula)
   */
  private calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371000; // Earth radius in meters
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRadians(degrees: number): number {
    return degrees * Math.PI / 180;
  }

  /**
   * Create temporary access grant
   */
  createTemporaryGrant(params: {
    grantedTo: string;
    grantedToName: string;
    grantedBy: string;
    grantedByName: string;
    resourceType: 'pfile' | 'document' | 'collection';
    resourceId: string;
    resourceName: string;
    accessLevel: 'view' | 'edit' | 'full';
    reason: string;
    durationHours: number;
    maxAccesses?: number;
    restrictions?: PolicyAction['restrictions'];
  }): TemporaryAccessGrant {
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + params.durationHours);

    return {
      id: `grant_${Date.now()}`,
      grantedTo: params.grantedTo,
      grantedToName: params.grantedToName,
      grantedBy: params.grantedBy,
      grantedByName: params.grantedByName,
      resourceType: params.resourceType,
      resourceId: params.resourceId,
      resourceName: params.resourceName,
      accessLevel: params.accessLevel,
      reason: params.reason,
      grantedAt: new Date(),
      expiresAt,
      isActive: true,
      accessCount: 0,
      maxAccesses: params.maxAccesses,
      restrictions: params.restrictions,
    };
  }

  /**
   * Revoke temporary grant
   */
  revokeGrant(
    grant: TemporaryAccessGrant,
    revokedBy: string,
    reason: string
  ): TemporaryAccessGrant {
    return {
      ...grant,
      isActive: false,
      revokedAt: new Date(),
      revokedBy,
      revokeReason: reason,
    };
  }

  /**
   * Record biometric verification
   */
  recordBiometricVerification(params: {
    userId: string;
    method: BiometricVerification['method'];
    deviceId: string;
    confidence: number;
    isVerified: boolean;
    failureReason?: string;
  }): BiometricVerification {
    return {
      id: `bio_${Date.now()}`,
      userId: params.userId,
      method: params.method,
      verifiedAt: new Date(),
      deviceId: params.deviceId,
      confidence: params.confidence,
      isVerified: params.isVerified,
      failureReason: params.failureReason,
    };
  }

  /**
   * Create security incident
   */
  createSecurityIncident(params: {
    type: IncidentType;
    severity: SecurityIncident['severity'];
    userId?: string;
    userName?: string;
    resourceId?: string;
    resourceName?: string;
    description: string;
    ipAddress?: string;
    location?: GeoLocation;
  }): SecurityIncident {
    const shouldEscalate = params.severity === 'critical' ||
      (params.severity === 'high' && ['unauthorized_access', 'privilege_escalation', 'session_hijack'].includes(params.type));

    return {
      id: `incident_${Date.now()}`,
      type: params.type,
      severity: params.severity,
      timestamp: new Date(),
      userId: params.userId,
      userName: params.userName,
      resourceId: params.resourceId,
      resourceName: params.resourceName,
      description: params.description,
      ipAddress: params.ipAddress,
      location: params.location,
      isResolved: false,
      escalated: shouldEscalate,
      escalatedTo: shouldEscalate ? 'Security Administrator' : undefined,
    };
  }

  /**
   * Get time restriction status
   */
  getTimeRestrictionStatus(): {
    isWorkingHours: boolean;
    isWorkingDay: boolean;
    currentHour: number;
    currentDay: string;
    restrictionMessage?: string;
  } {
    const now = new Date();
    const hour = now.getHours();
    const day = now.getDay();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    const isWorkingHours = hour >= 8 && hour < 17;
    const isWorkingDay = day >= 1 && day <= 5;

    let restrictionMessage: string | undefined;
    if (!isWorkingDay) {
      restrictionMessage = 'Access to sensitive records is restricted on weekends';
    } else if (!isWorkingHours) {
      restrictionMessage = 'Access to sensitive records is restricted outside working hours (8:00-17:00)';
    }

    return {
      isWorkingHours,
      isWorkingDay,
      currentHour: hour,
      currentDay: dayNames[day],
      restrictionMessage,
    };
  }

  /**
   * Get sensitivity level label
   */
  getSensitivityLabel(level: SensitivityLevel): string {
    const labels: Record<SensitivityLevel, string> = {
      public: 'Public',
      internal: 'Internal Use Only',
      confidential: 'Confidential',
      secret: 'Secret',
      top_secret: 'Top Secret',
    };
    return labels[level];
  }

  /**
   * Get incident type label
   */
  getIncidentTypeLabel(type: IncidentType): string {
    const labels: Record<IncidentType, string> = {
      unauthorized_access: 'Unauthorized Access Attempt',
      failed_authentication: 'Failed Authentication',
      suspicious_activity: 'Suspicious Activity',
      policy_violation: 'Policy Violation',
      geofence_breach: 'Geofence Breach',
      after_hours_access: 'After Hours Access',
      bulk_download: 'Bulk Download Detected',
      privilege_escalation: 'Privilege Escalation',
      session_hijack: 'Session Hijack Attempt',
      brute_force: 'Brute Force Attack',
    };
    return labels[type];
  }
}

// Export singleton instance
export const accessControl = new AccessControlService();

export type { AccessControlService };
